<section class="home-welcome" aria-labelledby="home-welcome-title">
  @if (isLoading()) {
    <header class="home-welcome__hero">
      <div class="home-welcome__skeleton" aria-hidden="true">
        <span class="home-welcome__skeleton-line home-welcome__skeleton-line--title"></span>
        <span class="home-welcome__skeleton-line home-welcome__skeleton-line--subtitle"></span>
      </div>
    </header>
  } @else if (!hasChildren()) {
    <header class="home-welcome__hero home-welcome__hero--newcomer">
      @if (displayName()) {
        <h1 id="home-welcome-title" class="home-welcome__title">
          {{
            'app.components.homeWelcome.newcomer.greeting'
              | translate: { name: displayName() }
          }}
        </h1>
      } @else {
        <h1 id="home-welcome-title" class="home-welcome__title">
          {{ 'app.components.homeWelcome.newcomer.greetingFallback' | translate }}
        </h1>
      }
      <p class="home-welcome__subtitle">
        {{ 'app.components.homeWelcome.newcomer.subtitle' | translate }}
      </p>
    </header>

    @if (pendingInvite(); as inv) {
      <aside class="home-welcome__invite-banner" aria-live="polite">
        <div class="home-welcome__invite-banner-copy">
          <p class="home-welcome__invite-banner-title">
            {{ 'app.components.homeWelcome.newcomer.pendingInviteTitle' | translate }}
          </p>
          <p class="home-welcome__invite-banner-body">
            {{
              'app.components.homeWelcome.newcomer.pendingInviteBody'
                | translate: { name: inv.invitedByDisplayName }
            }}
          </p>
        </div>
        <a
          class="home-welcome__invite-banner-cta"
          [routerLink]="['/co-parent-invite']"
          [queryParams]="{ inviteId: inv.id }"
        >
          {{ 'app.components.homeWelcome.newcomer.pendingInviteCta' | translate }}
        </a>
      </aside>
    }

    <ol
      class="home-welcome__setup"
      [attr.aria-label]="'app.components.homeWelcome.newcomer.setupLabel' | translate"
    >
      @if (needsIdentity()) {
        <li class="home-welcome__setup-step home-welcome__setup-step--priority">
          <span class="home-welcome__setup-icon" aria-hidden="true">
            <span
              class="home-welcome__setup-glyph"
              style="mask-image: url('/public/svgs/identity-id.svg'); -webkit-mask-image: url('/public/svgs/identity-id.svg')"
            ></span>
          </span>
          <div class="home-welcome__setup-copy">
            <span class="home-welcome__setup-title">
              {{ 'app.components.homeWelcome.newcomer.verifyTitle' | translate }}
            </span>
            <span class="home-welcome__setup-hint">
              {{ 'app.components.homeWelcome.newcomer.verifyHint' | translate }}
            </span>
          </div>
          <button
            type="button"
            class="home-welcome__setup-link"
            (click)="goVerify()"
          >
            {{ 'app.components.homeWelcome.newcomer.verifyCta' | translate }}
            <span class="home-welcome__setup-chevron" aria-hidden="true"></span>
          </button>
        </li>
      }

      <li class="home-welcome__setup-step home-welcome__setup-step--primary">
        <span class="home-welcome__setup-icon" aria-hidden="true">
          <span
            class="home-welcome__setup-glyph"
            style="mask-image: url('/public/svgs/add-kid.svg'); -webkit-mask-image: url('/public/svgs/add-kid.svg')"
          ></span>
        </span>
        <div class="home-welcome__setup-copy">
          <span class="home-welcome__setup-title">
            {{ 'app.components.homeWelcome.newcomer.addChildTitle' | translate }}
          </span>
          <span class="home-welcome__setup-hint">
            @if (canAddChild()) {
              {{ 'app.components.homeWelcome.newcomer.addChildHint' | translate }}
            } @else if (showKidsUpgrade()) {
              {{ 'app.components.homeWelcome.newcomer.addChildUpgradeHint' | translate }}
            } @else {
              {{ 'app.components.homeWelcome.newcomer.addChildBlockedHint' | translate }}
            }
          </span>
        </div>
        @if (canAddChild()) {
          <button
            type="button"
            class="home-welcome__setup-link"
            (click)="openAddKid()"
          >
            {{ 'app.components.homeWelcome.newcomer.addChildCta' | translate }}
            <span class="home-welcome__setup-chevron" aria-hidden="true"></span>
          </button>
        } @else if (showKidsUpgrade()) {
          <button
            type="button"
            class="home-welcome__setup-link"
            (click)="openAddKid()"
          >
            {{ 'app.components.homeWelcome.newcomer.addChildUpgradeCta' | translate }}
            <span class="home-welcome__setup-chevron" aria-hidden="true"></span>
          </button>
        } @else {
          <span class="home-welcome__setup-link home-welcome__setup-link--muted">
            {{ 'app.components.homeWelcome.newcomer.addChildCta' | translate }}
          </span>
        }
      </li>

      <li class="home-welcome__setup-step">
        <span class="home-welcome__setup-icon" aria-hidden="true">
          <span
            class="home-welcome__setup-glyph"
            style="mask-image: url('/public/svgs/family-together.svg'); -webkit-mask-image: url('/public/svgs/family-together.svg')"
          ></span>
        </span>
        <div class="home-welcome__setup-copy">
          <span class="home-welcome__setup-title">
            {{ 'app.components.homeWelcome.newcomer.familyTitle' | translate }}
          </span>
          <span class="home-welcome__setup-hint">
            {{ 'app.components.homeWelcome.newcomer.familyHint' | translate }}
          </span>
        </div>
        <a routerLink="/family" class="home-welcome__setup-link">
          {{ 'app.components.homeWelcome.newcomer.familyCta' | translate }}
          <span class="home-welcome__setup-chevron" aria-hidden="true"></span>
        </a>
      </li>

      <li class="home-welcome__setup-step">
        <span class="home-welcome__setup-icon" aria-hidden="true">
          <span
            class="home-welcome__setup-glyph"
            style="mask-image: url('/public/svgs/hat-w.svg'); -webkit-mask-image: url('/public/svgs/hat-w.svg')"
          ></span>
        </span>
        <div class="home-welcome__setup-copy">
          <span class="home-welcome__setup-title">
            {{ 'app.components.homeWelcome.newcomer.coursesTitle' | translate }}
          </span>
          <span class="home-welcome__setup-hint">
            {{ 'app.components.homeWelcome.newcomer.coursesHint' | translate }}
          </span>
        </div>
        <a
          routerLink="/home/courses"
          class="home-welcome__setup-link"
          (click)="onDestinationClick(tabs.COURSES)"
        >
          {{ 'app.components.homeWelcome.newcomer.coursesCta' | translate }}
          <span class="home-welcome__setup-chevron" aria-hidden="true"></span>
        </a>
      </li>
    </ol>

    @if (!pendingInvite()) {
      <p class="home-welcome__recovery">
        {{ 'app.components.homeWelcome.newcomer.inviteRecovery' | translate }}
      </p>
    }
  } @else {
    <header class="home-welcome__hero">
      @if (displayName()) {
        <h1 id="home-welcome-title" class="home-welcome__title">
          {{ 'app.components.homeWelcome.greeting' | translate: { name: displayName() } }}
        </h1>
      } @else {
        <h1 id="home-welcome-title" class="home-welcome__title">
          {{ 'app.components.homeWelcome.greetingFallback' | translate }}
        </h1>
      }

      <p class="home-welcome__subtitle">
        {{ 'app.components.homeWelcome.subtitle' | translate }}
      </p>

      @if (selectedChildLabel()) {
        <p class="home-welcome__context">
          {{
            'app.components.homeWelcome.focusChild'
              | translate: { name: selectedChildLabel() }
          }}
        </p>
      }
    </header>

    @if (attentionTotal() > 0) {
      <aside class="home-welcome__attention" aria-live="polite">
        <p class="home-welcome__attention-label">
          {{ 'app.components.homeWelcome.attentionTitle' | translate }}
        </p>
        <p class="home-welcome__attention-copy">
          {{
            'app.components.homeWelcome.attentionSummary'
              | translate
                : {
                    duties: pendingDutiesTotal(),
                    tasks: incompleteTasksTotal()
                  }
          }}
        </p>
      </aside>
    }

    <nav
      class="home-welcome__destinations"
      [attr.aria-label]="'app.components.homeWelcome.destinationsLabel' | translate"
    >
      <a
        routerLink="/home/assignments"
        class="home-welcome__destination home-welcome__destination--duties"
        (click)="onDestinationClick(tabs.DUTIES)"
      >
        <span class="home-welcome__destination-icon" aria-hidden="true">
          <span
            class="home-welcome__destination-glyph"
            style="mask-image: url('/public/svgs/calendar.svg'); -webkit-mask-image: url('/public/svgs/calendar.svg')"
          ></span>
        </span>
        <span class="home-welcome__destination-copy">
          <span class="home-welcome__destination-title">
            {{ 'app.components.homeWelcome.dutiesTitle' | translate }}
          </span>
          <span class="home-welcome__destination-hint">
            @if (pendingDutiesTotal() > 0) {
              {{
                'app.components.homeWelcome.dutiesHintPending'
                  | translate: { count: pendingDutiesTotal() }
              }}
            } @else {
              {{ 'app.components.homeWelcome.dutiesHint' | translate }}
            }
          </span>
        </span>
        <span class="home-welcome__destination-chevron" aria-hidden="true"></span>
      </a>

      <a
        routerLink="/home/courses"
        class="home-welcome__destination home-welcome__destination--courses"
        (click)="onDestinationClick(tabs.COURSES)"
      >
        <span class="home-welcome__destination-icon" aria-hidden="true">
          <span
            class="home-welcome__destination-glyph"
            style="mask-image: url('/public/svgs/hat-w.svg'); -webkit-mask-image: url('/public/svgs/hat-w.svg')"
          ></span>
        </span>
        <span class="home-welcome__destination-copy">
          <span class="home-welcome__destination-title">
            {{ 'app.components.homeWelcome.coursesTitle' | translate }}
          </span>
          <span class="home-welcome__destination-hint">
            {{ 'app.components.homeWelcome.coursesHint' | translate }}
          </span>
        </span>
        <span class="home-welcome__destination-chevron" aria-hidden="true"></span>
      </a>
    </nav>
  }
</section>
